/**
 * The practice's own claim items: the codes, names and fees behind everything it bills.
 *
 * CatTrax ships a standard list of MBS items, which nobody can edit and none of which carries a price - what
 * a practice charges is the practice's own business. Pricing one of those, or adding an item CatTrax has
 * never heard of, creates the practice's own copy, and that copy is what the pickers offer from then on.
 *
 * Until this screen those copies could only be made side-on: a clinician typing a fee while recording an item
 * against a patient. That worked, but it left the practice's price list with no page of its own - nowhere to
 * see what was on it, correct a fee, or retire something. Practice-wide, and not admin-only: the person who
 * knows what a consult costs is rarely the person with the admin flag.
 */
app.component("claimItemsSetting", {
	templateUrl: "app/src/js/components/claimItemsSetting/claimItemsSetting.html",
	controller: "claimItemsController",
	bindings: { practiceId: "<" },
});

app.controller("claimItemsController", [
	"$scope",
	"TyroAPI",
	"TyroClaimableItems",
	"ExceptionAPI",
	"Session",
	"$modal",
	"$q",
	function ($scope, TyroAPI, TyroClaimableItems, ExceptionAPI, Session, $modal, $q) {

		$scope.API = TyroAPI;
		$scope.items = [];
		$scope.referenceItems = [];
		$scope.isLoading = false;
		$scope.loadError = null;
		$scope.saveStatus = "";
		$scope.filter = "";

		$scope.newItem = blankItem();

		function blankItem() {
			return { fromReference: null, item_code: "", name: "", fee: null };
		}

		this.$onInit = function () {
			// The settings dialog can be pointed at any practice the user belongs to, not only the one they
			// are working in today - so every call names the practice rather than relying on the session.
			$scope.practiceId = this.practiceId || (Session.practice && Session.practice.id);
			const practice = (Session.practiceOptions || []).find(option => option.id === $scope.practiceId)
				|| Session.practice || {};
			$scope.practiceName = practice.name || "";

			loadItems();
		};

		function loadItems() {
			$scope.isLoading = true;
			$scope.loadError = null;

			// Both lists in one read, then split: the practice's own items are what this screen edits, and the
			// CatTrax ones are what the add row searches so a standard item can be priced without re-keying
			// its code and name.
			$scope.API.getClaimableItems(null, $scope.practiceId)
				.then(function (response) {
					const all = response.data || [];
					$scope.items = all.filter(isPracticeItem).map(normaliseAvailable).sort(byItemCode);
					$scope.referenceItems = all.filter(item => !isPracticeItem(item))
						.map(TyroClaimableItems.tokenise)
						.sort(byItemCode);
					rememberSaved();
				})
				.catch(function (error) {
					$scope.loadError = ExceptionAPI.serverMessage(error)
						|| "The claim item list could not be loaded. Close settings and try again.";
				})
				.finally(function () {
					$scope.isLoading = false;
				});
		}

		// Legacy rows written before the type split carry no item_type and are CatTrax defaults, which the
		// backend also treats as SYSTEM.
		function isPracticeItem(item) {
			return item.item_type === "PRACTICE";
		}

		// An item saved before the flag existed carries no answer, and everything reading it treats that as
		// available. Bound straight to a checkbox it would read as off, and the row would offer to turn on
		// something that is already on.
		function normaliseAvailable(item) {
			item.available = item.available !== false;
			return item;
		}

		function byItemCode(a, b) {
			return (a.item_code || "").localeCompare(b.item_code || "", undefined, { numeric: true });
		}

		// --- What is on the list ---

		$scope.visibleItems = function () {
			const terms = ($scope.filter || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
			if (!terms.length) {
				return $scope.items;
			}
			return $scope.items.filter(function (item) {
				const haystack = ((item.item_code || "") + " " + (item.name || "")).toLowerCase();
				return terms.every(term => haystack.indexOf(term) !== -1);
			});
		};

		// Whether this item is the practice's own price for something CatTrax also ships, as against an item
		// only this practice has. Said on the row because the two behave differently in the pickers: a priced
		// copy supersedes the CatTrax item whose code it shares, and the standard one stops being offered.
		$scope.overridesStandardItem = function (item) {
			return $scope.referenceItems.some(reference => reference.item_code === item.item_code);
		};

		// --- Editing what is already there ---

		// Each item as last persisted, so Save sends only what changed.
		let saved = {};

		function rememberSaved() {
			saved = {};
			$scope.items.forEach(function (item) {
				saved[item.tyro_claimable_item_id] = snapshot(item);
			});
		}

		function snapshot(item) {
			return { name: item.name, fee: item.fee, available: item.available !== false };
		}

		function hasChanged(item) {
			const before = saved[item.tyro_claimable_item_id];
			if (!before) {
				return true;
			}
			return before.name !== item.name
				|| before.fee !== item.fee
				|| before.available !== (item.available !== false);
		}

		$scope.hasUnsavedChanges = function () {
			return $scope.items.some(hasChanged);
		};

		// Drops a stale "saved" tick the moment something else is edited.
		$scope.$watch($scope.hasUnsavedChanges, function (dirty) {
			if (dirty) {
				$scope.saveStatus = "";
			}
		});

		// An item with no name or no fee cannot be saved - both are required by the API, and an unpriced
		// practice item is worse than no practice item at all: it supersedes the CatTrax one and prices
		// nothing, so the fee field simply comes up empty wherever it is picked.
		$scope.isItemValid = function (item) {
			return !!(item.name || "").trim() && item.fee !== null && item.fee !== undefined && item.fee >= 0;
		};

		$scope.canSave = function () {
			return $scope.hasUnsavedChanges() && $scope.items.every($scope.isItemValid);
		};

		function requestFor(item) {
			return {
				item_code: item.item_code,
				name: item.name,
				description: item.description,
				funder: item.funder,
				available: item.available !== false,
				fee: item.fee,
				search_term: item.search_term,
				other_search_terms: item.other_search_terms,
			};
		}

		function saveChanged() {
			const changed = $scope.items.filter(hasChanged);
			return $q.all(changed.map(item =>
				$scope.API.updateClaimableItem(item.tyro_claimable_item_id, requestFor(item), $scope.practiceId)
			)).then(() => changed.length);
		}

		$scope.save = function () {
			if (!$scope.hasUnsavedChanges()) {
				$scope.saveStatus = "No changes to save";
				return;
			}
			if (!$scope.canSave()) {
				return;
			}

			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";

			saveChanged()
				.then(function () {
					rememberSaved();
					// The pickers hold this list for the life of the page, so a price edited here has to reach
					// the claim and invoice screens without a reload.
					TyroClaimableItems.clear();
					$scope.saveStatus = "Claim items saved";
				})
				.catch(function (error) {
					$scope.saveStatus = "Error saving claim items";
					showError(error, "Your changes could not be saved.");
				})
				.finally(function () {
					$scope.API.isProcessing = false;
				});
		};

		// --- Adding one ---

		// Starting from a CatTrax item fills the code and name so only the fee has to be typed. The fields
		// stay editable afterwards: the standard name is often not what a practice calls the thing.
		$scope.onReferenceItemSelected = function () {
			const reference = $scope.newItem.fromReference;
			if (!reference) {
				return;
			}
			$scope.newItem.item_code = reference.item_code;
			$scope.newItem.name = reference.name;
		};

		$scope.referenceItemLabel = function (item) {
			return TyroClaimableItems.label(item);
		};

		$scope.searchReferenceItems = function (search) {
			return TyroClaimableItems.search($scope.referenceItems, search);
		};

		// The code the practice already holds, if this one would land on top of it. Adding restates that item
		// rather than making a second row - a practice holds one item per code - so it is said before the
		// button is pressed rather than discovered afterwards.
		$scope.replacesExistingItem = function () {
			const code = ($scope.newItem.item_code || "").trim();
			return code ? $scope.items.find(item => item.item_code === code) : null;
		};

		$scope.canAddItem = function () {
			return !!($scope.newItem.item_code || "").trim()
				&& !!($scope.newItem.name || "").trim()
				&& $scope.newItem.fee !== null && $scope.newItem.fee !== undefined && $scope.newItem.fee >= 0;
		};

		$scope.addItem = function () {
			if (!$scope.canAddItem() || !$scope.canSaveBeforeAdding()) {
				return;
			}

			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";

			const request = {
				item_code: ($scope.newItem.item_code || "").trim(),
				name: ($scope.newItem.name || "").trim(),
				fee: $scope.newItem.fee,
				available: true,
			};
			// Carried over where the item was started from a CatTrax one, so the practice's copy stays
			// searchable by everything the original was searchable by.
			const reference = $scope.newItem.fromReference;
			if (reference && reference.item_code === request.item_code) {
				request.description = reference.description;
				request.funder = reference.funder;
				request.search_term = reference.search_term;
				request.other_search_terms = reference.other_search_terms;
			}

			// Adding reloads the list from the server, so pending edits are flushed first rather than thrown
			// away by the reload.
			saveChanged()
				.then(() => $scope.API.createClaimableItem(request, $scope.practiceId))
				.then(function () {
					$scope.newItem = blankItem();
					TyroClaimableItems.clear();
					$scope.saveStatus = "Item added";
					loadItems();
				})
				.catch(function (error) {
					$scope.saveStatus = "Error adding item";
					showError(error, "The item could not be added.");
				})
				.finally(function () {
					$scope.API.isProcessing = false;
				});
		};

		// Adding flushes pending edits with it, so it is blocked by an invalid row for the same reason Save is.
		$scope.canSaveBeforeAdding = function () {
			return $scope.items.every($scope.isItemValid);
		};

		// --- Removing one ---

		$scope.removeItem = function (item) {
			$scope.itemToRemove = item;
			$scope.generalModal = $modal({
				scope: $scope,
				templateUrl: "app/src/views/templates/modal.general.tpl.html",
				show: true,
				title: "Remove " + item.item_code + "?",
				// Short, and only the part that is not obvious from the screen: what happens to the work
				// already billed under this item, and what takes its place in the pickers.
				content: "Anything already recorded or claimed keeps the fee it was recorded with"
					+ ($scope.overridesStandardItem(item)
						? ", and the standard CatTrax item is offered again, unpriced."
						: "."),
				backdrop: true,
				keyboard: false,
			});
		};

		$scope.onGeneralClick = function () {
			if ($scope.generalModal) {
				$scope.generalModal.hide();
			}
			const item = $scope.itemToRemove;
			$scope.itemToRemove = null;
			if (!item) {
				return;
			}

			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";

			// Removing reloads the list from the server, so pending edits to the other rows are flushed
			// first rather than thrown away by the reload - the same as adding. The row being removed is
			// left out: saving it only to delete it is pointless, and an invalid edit on it must not block.
			const flushed = $scope.items
				.filter(other => other !== item && hasChanged(other) && $scope.isItemValid(other))
				.map(other =>
					$scope.API.updateClaimableItem(other.tyro_claimable_item_id, requestFor(other), $scope.practiceId));

			$q.all(flushed)
				.catch(function (error) {
					// Nothing is removed over an edit that would not save: the reload would throw the edit
					// away, and the message has to say which step failed.
					$scope.saveStatus = "Error saving changes";
					showError(error, "A change to another item could not be saved, so nothing was removed.");
					return $q.reject(null);
				})
				.then(() => $scope.API.deleteClaimableItem(item.tyro_claimable_item_id, $scope.practiceId)
					.then(function () {
						TyroClaimableItems.clear();
						$scope.saveStatus = "Item removed";
						loadItems();
					}, function (error) {
						$scope.saveStatus = "Error removing item";
						showError(error, "The item could not be removed.");
					}))
				.catch(angular.noop)
				.finally(function () {
					$scope.API.isProcessing = false;
				});
		};

		// The server's message is the actionable one on these paths - "Your practice already has item code
		// 104" names the thing to go and fix - so it is shown rather than replaced by the generic wording.
		function showError(error, lead) {
			if (error) {
				console.error("Claim item request failed:", error.status, ExceptionAPI.serverMessage(error));
			}
			$scope.alertMessage = lead;
			$scope.alertDetail = ExceptionAPI.serverMessage(error);
			$scope.serverErrorModal = $modal({
				scope: $scope,
				templateUrl: "app/src/views/templates/modal.server-error.tpl.html",
				show: true,
				title: "CatTrax has encountered an error",
			});
		}
	},
]);
